import React, { useState, useCallback, useMemo, useEffect, useRef, Fragment, useDeferredValue } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Eye, Copy, Check, Loader2, Pencil, Plus, X, Mail, MailX, Trash2, ExternalLink, ArrowRightLeft, ChevronDown, ChevronRight, Globe, Lock, MoreVertical, AlertTriangle, Tag } from 'lucide-react';
import {
  TopHeader,
  Button,
  EmptyState,
  RsvpBadge,
  Badge,
  Select,
  Checkbox,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableCheckbox,
  BulkActionBar,
  TextInput,
  Alert,
  ConfirmModal,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
  Tooltip,
} from '../../components';
import { BrowseFilters } from '../../components/browse/BrowseFilters';
import { UpgradeModal } from '../../components/modals/UpgradeModal';
import { AddGuestModal } from '../../components/guests/AddGuestModal';
import { BulkImportModal } from '../../components/guests/BulkImportModal';
import { TagSelector } from '../../components/guests/TagSelector';
import { RsvpStatsSection } from '../../components/guests/RsvpStatsSection';
import { useAllGuests, useInvites, useAddInviteGuest, useBulkImportGuests, useUpdateInviteGuest, useRemoveInviteGuest, useBulkRemoveGuests, useSendGuestEmail, useAssignGuestsToInvite, useBulkUpdateRsvp, useRsvpSummary, useGuestTags, useCreateGuestTag, useBulkAssignTag } from '../../api/hooks/useInvites';
import { useBillingUsage } from '../../api/hooks/useBilling';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSelection } from '../../hooks/useSelection';
import type { GuestWithInvite, InviteListItem, RsvpStatus, GuestTag, PublishGateError, CustomQuestion } from '../../types';
import type { FilterCategory, SelectedFilter } from '../../types';
import { TAG_BADGE_CLASS, getTagBg, getTagDot } from '../../utils/tagStyles';

const RSVP_FILTER_CATEGORY: FilterCategory = {
  id: 'rsvp',
  label: 'RSVP',
  options: [
    { id: 'yes', label: 'Attending' },
    { id: 'no', label: 'Not Attending' },
    { id: 'maybe', label: 'Maybe' },
    { id: 'pending', label: 'Not Replied' },
  ],
};

const RSVP_OPTIONS: { value: RsvpStatus; label: string }[] = [
  { value: 'pending', label: 'Awaiting' },
  { value: 'yes', label: 'Attending' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'no', label: 'Not Attending' },
];

const RSVP_BADGE_VARIANTS: Record<RsvpStatus, 'success' | 'error' | 'warning' | 'default'> = {
  pending: 'default',
  yes: 'success',
  no: 'error',
  maybe: 'warning',
};

const GuestRow = React.memo(function GuestRow({
  guest,
  selected,
  onToggle,
  onCopyLink,
  onWhatsApp,
  onEdit,
  onRemove,
  onSendEmail,
  onRsvpChange,
  onMarkAsInvited,
  isSending,
  showInviteColumn,
  onInviteClick,
  isAdmin,
  plusOneCount = 0,
  isExpanded = false,
  onToggleExpand,
  isIndented = false,
  customQuestions,
}: {
  guest: GuestWithInvite;
  selected: boolean;
  onToggle: () => void;
  onCopyLink: () => void;
  onWhatsApp: () => void;
  onEdit: () => void;
  onRemove: () => void;
  onSendEmail: () => void;
  onRsvpChange: (status: RsvpStatus) => void;
  onMarkAsInvited: (invited: boolean) => void;
  isSending: boolean;
  showInviteColumn: boolean;
  onInviteClick: () => void;
  isAdmin: boolean;
  plusOneCount?: number;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  isIndented?: boolean;
  customQuestions?: CustomQuestion[];
}) {
  const [copied, setCopied] = useState(false);
  const [showRsvpDropdown, setShowRsvpDropdown] = useState(false);
  const [showInviteStatusDropdown, setShowInviteStatusDropdown] = useState(false);
  const rsvpDropdownRef = useRef<HTMLDivElement>(null);
  const inviteStatusDropdownRef = useRef<HTMLDivElement>(null);
  const rsvpTriggerRef = useRef<HTMLButtonElement>(null);
  const inviteTriggerRef = useRef<HTMLButtonElement>(null);
  const [rsvpPos, setRsvpPos] = useState({ top: 0, left: 0, openUpward: false });
  const [invitePos, setInvitePos] = useState({ top: 0, left: 0, openUpward: false });

  useEffect(() => {
    if (!showRsvpDropdown && !showInviteStatusDropdown) return;
    const handler = (e: MouseEvent) => {
      if (showRsvpDropdown && rsvpDropdownRef.current && !rsvpDropdownRef.current.contains(e.target as Node)) {
        setShowRsvpDropdown(false);
      }
      if (showInviteStatusDropdown && inviteStatusDropdownRef.current && !inviteStatusDropdownRef.current.contains(e.target as Node)) {
        setShowInviteStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showRsvpDropdown, showInviteStatusDropdown]);

  const handleCopy = () => {
    onCopyLink();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isDraft = guest.invite.status === 'draft';
  const sentToday = guest.email_sent_at && new Date(guest.email_sent_at) > new Date(new Date().setHours(0, 0, 0, 0));
  const emailDisabled = Boolean(isSending || guest.unsubscribed_at || (!isAdmin && (isDraft || !!sentToday)));
  const emailDisabledReason = isSending ? 'Sending in progress'
    : guest.unsubscribed_at ? 'Guest has unsubscribed'
    : isDraft ? 'Invite is still a draft'
    : sentToday ? 'Already emailed today'
    : '';

  const isPlusOne = !!guest.parent_guest_id;

  return (
    <TableRow>
      <TableCell className="w-10 !pr-0">
        <TableCheckbox checked={selected} onChange={onToggle} />
      </TableCell>
      <TableCell>
        <div className={isIndented ? 'pl-8' : ''}>
          <div className="flex items-center gap-2">
            <div className="font-medium text-text-primary">{guest.name || 'Unnamed guest'}</div>
            {guest.unsubscribed_at && (
              <Tooltip content="Unsubscribed from emails">
                <MailX size={16} className="text-error" />
              </Tooltip>
            )}
          </div>
          {guest.email && (
            <div className="text-sm text-text-secondary">{guest.email}</div>
          )}
          {showInviteColumn && (
            <div className="flex items-center gap-1 mt-0.5">
              {guest.invite.visibility === 'public' ? (
                <Globe size={11} className="text-text-tertiary shrink-0" />
              ) : (
                <Lock size={11} className="text-text-tertiary shrink-0" />
              )}
              {guest.invite.is_system ? (
                <span className="text-xs text-text-tertiary italic">{guest.invite.title}</span>
              ) : (
                <button onClick={onInviteClick} className="text-xs text-text-tertiary hover:text-primary hover:underline transition-colors">
                  {guest.invite.title}
                </button>
              )}
            </div>
          )}
          {isPlusOne && guest.parent_guest_name && (
            <div className="text-xs text-text-tertiary">+1 of {guest.parent_guest_name}</div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="relative" ref={rsvpDropdownRef}>
          <button
            ref={rsvpTriggerRef}
            onClick={() => {
              if (!showRsvpDropdown && rsvpTriggerRef.current) {
                const rect = rsvpTriggerRef.current.getBoundingClientRect();
                const estimatedH = 160;
                const openUpward = window.innerHeight - rect.bottom - 8 < estimatedH;
                setRsvpPos({ top: openUpward ? rect.top - 4 : rect.bottom + 4, left: rect.left, openUpward });
              }
              setShowRsvpDropdown(!showRsvpDropdown);
            }}
            data-builder="guest-rsvp-trigger"
            className="cursor-pointer hover:opacity-80 transition-opacity"
          >
            <Badge variant={RSVP_BADGE_VARIANTS[guest.rsvp_status]}>
              {RSVP_OPTIONS.find(o => o.value === guest.rsvp_status)?.label}
              <ChevronDown
                size={10}
                className={`transition-transform duration-200 ${showRsvpDropdown ? 'rotate-180' : ''}`}
              />
            </Badge>
          </button>
          <AnimatePresence>
            {showRsvpDropdown && (
              <motion.div
                initial={{ opacity: 0, y: rsvpPos.openUpward ? 10 : -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: rsvpPos.openUpward ? 10 : -10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="fixed z-[9999] flex flex-col bg-background-card border border-border rounded-lg shadow-lg py-1 min-w-[150px]"
                style={{
                  top: rsvpPos.openUpward ? undefined : rsvpPos.top,
                  bottom: rsvpPos.openUpward ? window.innerHeight - rsvpPos.top : undefined,
                  left: rsvpPos.left,
                }}
              >
                {RSVP_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      if (opt.value !== guest.rsvp_status) {
                        onRsvpChange(opt.value);
                      }
                      setShowRsvpDropdown(false);
                    }}
                    data-builder={`guest-rsvp-option-${opt.value}`}
                    className={`text-left px-3 py-1.5 text-sm transition-colors whitespace-nowrap ${
                      opt.value === guest.rsvp_status
                        ? 'bg-primary/10 text-primary'
                        : 'text-text-primary hover:bg-background-surface'
                    }`}
                  >
                    <RsvpBadge status={opt.value} />
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2 text-text-secondary">
          <Eye size={14} />
          {guest.view_count}
        </div>
      </TableCell>
      <TableCell>
        {!isPlusOne && (
          <div className="relative" ref={inviteStatusDropdownRef}>
            <button
              ref={inviteTriggerRef}
              onClick={() => {
                if (!showInviteStatusDropdown && inviteTriggerRef.current) {
                  const rect = inviteTriggerRef.current.getBoundingClientRect();
                  const estimatedH = 130;
                  const openUpward = window.innerHeight - rect.bottom - 8 < estimatedH;
                  setInvitePos({ top: openUpward ? rect.top - 4 : rect.bottom + 4, left: rect.left, openUpward });
                }
                setShowInviteStatusDropdown(!showInviteStatusDropdown);
              }}
              data-builder="guest-status-trigger"
              className="cursor-pointer hover:opacity-80 transition-opacity"
            >
              {guest.email_sent_at ? (
                <Tooltip content={`Invited ${new Date(guest.email_sent_at).toLocaleDateString()}`}>
                  <Badge variant="success">
                    Invited
                    <ChevronDown
                      size={10}
                      className={`transition-transform duration-200 ${showInviteStatusDropdown ? 'rotate-180' : ''}`}
                    />
                  </Badge>
                </Tooltip>
              ) : (
                <Badge variant={guest.rsvp_submitted_at ? 'success' : 'default'}>
                  {guest.invite.visibility === 'public' && guest.rsvp_submitted_at
                    ? 'Invited - Public'
                    : guest.invite.visibility === 'private' && guest.rsvp_submitted_at
                    ? 'Invited - Private Link'
                    : guest.email ? 'Not invited' : 'No email'}
                  <ChevronDown
                    size={10}
                    className={`transition-transform duration-200 ${showInviteStatusDropdown ? 'rotate-180' : ''}`}
                  />
                </Badge>
              )}
            </button>
            <AnimatePresence>
              {showInviteStatusDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: invitePos.openUpward ? 10 : -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: invitePos.openUpward ? 10 : -10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="fixed z-[9999] flex flex-col bg-background-card border border-border rounded-lg shadow-lg py-1 min-w-[170px]"
                  style={{
                    top: invitePos.openUpward ? undefined : invitePos.top,
                    bottom: invitePos.openUpward ? window.innerHeight - invitePos.top : undefined,
                    left: invitePos.left,
                  }}
                >
                  <button
                    onClick={() => {
                      if (!guest.email_sent_at && !guest.marked_as_invited_at && guest.invite.status !== 'draft') onMarkAsInvited(true);
                      setShowInviteStatusDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                      guest.email_sent_at || guest.marked_as_invited_at
                        ? 'bg-primary/10 text-primary'
                        : 'text-text-primary hover:bg-background-surface'
                    }`}
                  >
                    <Check size={14} />
                    Invited
                  </button>
                  {guest.invite.visibility === 'public' && (
                    <button
                      onClick={() => setShowInviteStatusDropdown(false)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                        !guest.email_sent_at && guest.rsvp_submitted_at
                          ? 'bg-primary/10 text-primary'
                          : 'text-text-primary hover:bg-background-surface'
                      }`}
                    >
                      <Globe size={14} />
                      Invited - Public
                    </button>
                  )}
                  {guest.invite.visibility === 'private' && (
                    <button
                      onClick={() => setShowInviteStatusDropdown(false)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                        !guest.email_sent_at && guest.rsvp_submitted_at
                          ? 'bg-primary/10 text-primary'
                          : 'text-text-primary hover:bg-background-surface'
                      }`}
                    >
                      <Lock size={14} />
                      Invited - Private Link
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (guest.marked_as_invited_at) onMarkAsInvited(false);
                      setShowInviteStatusDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                      !guest.email_sent_at && !guest.marked_as_invited_at && !(guest.invite.visibility === 'public' && guest.rsvp_submitted_at)
                        ? 'bg-primary/10 text-primary'
                        : 'text-text-primary hover:bg-background-surface'
                    }`}
                  >
                    <X size={14} />
                    Not invited
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </TableCell>
      <TableCell>
        {guest.tags && guest.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {guest.tags.map((tag) => (
              <span
                key={tag.id}
                className={`${TAG_BADGE_CLASS} ${getTagBg(tag.color)}`}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </TableCell>
      <TableCell>
        {plusOneCount > 0 ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleExpand?.(); }}
            className="inline-flex items-center gap-1 pl-2 pr-2.5 py-1 rounded-full bg-primary/15 text-primary text-xs font-semibold hover:bg-primary/25 transition-colors"
          >
            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            +{plusOneCount}
          </button>
        ) : null}
      </TableCell>
      <TableCell>
        {(guest.dietary_preference || (guest.allergies && guest.allergies.length > 0) || guest.dietary_notes || (guest.custom_answers && Object.keys(guest.custom_answers).length > 0)) && (
          <div className="text-text-secondary text-xs space-y-0.5">
            {guest.dietary_preference && (
              <div className="truncate">Dietary: {guest.dietary_preference}</div>
            )}
            {guest.allergies && guest.allergies.length > 0 && (
              <div className="flex items-start gap-1">
                <AlertTriangle size={11} className="text-error flex-shrink-0 mt-0.5" />
                <span className="break-words">Allergies: {guest.allergies.join(', ')}</span>
              </div>
            )}
            {!guest.dietary_preference && !(guest.allergies && guest.allergies.length > 0) && guest.dietary_notes && (
              <div className="truncate">{guest.dietary_notes}</div>
            )}
            {guest.custom_answers && customQuestions && customQuestions.map((q) => {
              const answer = guest.custom_answers![q.id];
              if (!answer) return null;
              const displayAnswer = answer.includes('|||') ? answer.split('|||').join(', ') : answer;
              return (
                <div key={q.id} className="truncate">
                  <span className="text-text-tertiary">{q.label || 'Q'}:</span> {displayAnswer}
                </div>
              );
            })}
          </div>
        )}
      </TableCell>
      <TableCell className="sticky right-0 bg-background-card shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]">
        <Dropdown>
          <DropdownTrigger asChild>
            <button data-builder="guest-actions-menu" className="p-2 rounded-lg hover:bg-background-elevated transition-colors">
              <MoreVertical size={16} className="text-text-secondary" />
            </button>
          </DropdownTrigger>
          <DropdownMenu align="right">
            <DropdownItem icon={<Pencil size={14} />} onClick={onEdit}>
              Edit
            </DropdownItem>
            {!isPlusOne && (
              <DropdownItem icon={copied ? <Check size={14} /> : <Copy size={14} />} onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy Link'}
              </DropdownItem>
            )}
            {!isPlusOne && (
              <DropdownItem
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>}
                onClick={onWhatsApp}
              >
                Send via WhatsApp
              </DropdownItem>
            )}
            {guest.email && !isPlusOne && (
              emailDisabled ? (
                <Tooltip content={emailDisabledReason}>
                  <div>
                    <DropdownItem
                      icon={isSending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                      onClick={onSendEmail}
                      disabled
                    >
                      {guest.email_sent_at ? 'Resend Email' : 'Send Email'}
                    </DropdownItem>
                  </div>
                </Tooltip>
              ) : (
                <DropdownItem
                  icon={<Mail size={14} />}
                  onClick={onSendEmail}
                >
                  {guest.email_sent_at ? 'Resend Email' : 'Send Email'}
                </DropdownItem>
              )
            )}
            <DropdownDivider />
            <DropdownItem icon={<Trash2 size={14} />} onClick={onRemove} variant="danger">
              Remove
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </TableCell>
    </TableRow>
  );
}, (prev, next) =>
  prev.guest === next.guest &&
  prev.selected === next.selected &&
  prev.isSending === next.isSending &&
  prev.showInviteColumn === next.showInviteColumn &&
  prev.isAdmin === next.isAdmin &&
  prev.plusOneCount === next.plusOneCount &&
  prev.isExpanded === next.isExpanded &&
  prev.isIndented === next.isIndented &&
  prev.customQuestions === next.customQuestions
);

function EditGuestModal({
  guest,
  invites: allInvites,
  tags: availableTags,
  customQuestions,
  onClose,
  onSave,
  isSaving,
}: {
  guest: GuestWithInvite;
  invites: InviteListItem[];
  tags?: GuestTag[];
  customQuestions?: CustomQuestion[];
  onClose: () => void;
  onSave: (data: { name: string; email: string; inviteId: number; dietaryPreference: string; allergies: string[]; rsvpStatus: RsvpStatus; markAsInvited?: boolean; tagIds?: number[]; customAnswers?: Record<string, string> }) => Promise<void>;
  isSaving: boolean;
}) {
  const invites = useMemo(() => allInvites.filter((i) => !i.is_system), [allInvites]);
  const DIETARY_OPTIONS = ['None', 'Vegetarian', 'Pescatarian', 'Vegan', 'Other'] as const;
  const ALLERGY_OPTIONS = ['Nuts', 'Dairy', 'Gluten', 'Shellfish', 'Eggs', 'Soy'] as const;

  const initDietPref = (() => {
    const v = guest.dietary_preference || '';
    if (!v) return { selection: 'None', custom: '' };
    const std = DIETARY_OPTIONS.find(o => o !== 'Other' && o !== 'None' && o === v);
    if (std) return { selection: std, custom: '' };
    if (v) return { selection: 'Other' as string, custom: v };
    return { selection: 'None', custom: '' };
  })();
  const initAllergyState = (() => {
    const vals = guest.allergies || [];
    const std: string[] = [];
    const cust: string[] = [];
    for (const a of vals) {
      if ((ALLERGY_OPTIONS as readonly string[]).includes(a)) std.push(a); else cust.push(a);
    }
    return { checked: std, custom: cust.join(', ') };
  })();

  const [name, setName] = useState(guest.name);
  const [email, setEmail] = useState(guest.email || '');
  const [dietarySelection, setDietarySelection] = useState(initDietPref.selection);
  const [dietaryCustom, setDietaryCustom] = useState(initDietPref.custom);
  const [allergyChecked, setAllergyChecked] = useState<string[]>(initAllergyState.checked);
  const [allergyHasOther, setAllergyHasOther] = useState(initAllergyState.custom.length > 0);
  const [allergyCustom, setAllergyCustom] = useState(initAllergyState.custom);
  const [selectedInviteId, setSelectedInviteId] = useState(guest.invite.id);
  const [rsvpStatus, setRsvpStatus] = useState<RsvpStatus>(guest.rsvp_status);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(
    guest.tags?.map(t => t.id) || []
  );
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>(guest.custom_answers || {});
  const isPublicInvited = !guest.email_sent_at && guest.invite.visibility === 'public' && guest.rsvp_submitted_at;
  const [inviteStatus, setInviteStatus] = useState<string>(
    guest.email_sent_at ? 'invited' : isPublicInvited ? 'public' : 'not_invited'
  );
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    const wasInvited = !!guest.email_sent_at;
    const nowInvited = inviteStatus === 'invited';
    const markAsInvited = !wasInvited && nowInvited;
    const markAsNotInvited = wasInvited && !nowInvited;

    const builtDietPref = dietarySelection === 'Other' ? dietaryCustom.trim() : dietarySelection === 'None' ? '' : dietarySelection;
    const builtAllergies = [...allergyChecked, ...(allergyHasOther && allergyCustom.trim() ? allergyCustom.split(',').map(s => s.trim()).filter(Boolean) : [])];

    try {
      await onSave({
        name: name.trim(),
        email: email.trim(),
        inviteId: selectedInviteId,
        dietaryPreference: builtDietPref,
        allergies: builtAllergies,
        rsvpStatus,
        markAsInvited: markAsInvited ? true : markAsNotInvited ? false : undefined,
        tagIds: selectedTagIds,
        customAnswers: customQuestions?.length ? customAnswers : undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update guest');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-md bg-background-card rounded-xl border border-border shadow-xl mx-4 max-h-[90dvh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <h2 className="text-base font-semibold text-text-primary">Edit Guest</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-background-elevated transition-colors">
            <X size={18} className="text-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Scrollable fields */}
          <div className="overflow-y-auto flex-1 px-4 py-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Name *</label>
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Email (optional)</label>
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Dietary Preference</label>
              <Select
                options={DIETARY_OPTIONS.map(opt => ({ value: opt, label: opt }))}
                value={dietarySelection}
                onChange={(val) => setDietarySelection(val)}
              />
              {dietarySelection === 'Other' && (
                <TextInput
                  value={dietaryCustom}
                  onChange={(e) => setDietaryCustom(e.target.value)}
                  placeholder="Please specify..."
                  className="mt-2"
                />
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Allergies</label>
              <div className="flex flex-wrap gap-2">
                {ALLERGY_OPTIONS.map(allergy => (
                  <Checkbox
                    key={allergy}
                    checked={allergyChecked.includes(allergy)}
                    onChange={() => setAllergyChecked(prev => prev.includes(allergy) ? prev.filter(a => a !== allergy) : [...prev, allergy])}
                    label={allergy}
                  />
                ))}
                <Checkbox
                  checked={allergyHasOther}
                  onChange={() => setAllergyHasOther(!allergyHasOther)}
                  label="Other"
                />
              </div>
              {allergyHasOther && (
                <TextInput
                  value={allergyCustom}
                  onChange={(e) => setAllergyCustom(e.target.value)}
                  placeholder="Please specify..."
                  className="mt-2"
                />
              )}
            </div>

            {customQuestions && customQuestions.length > 0 && (
              <div className="space-y-3">
                <label className="block text-xs font-medium text-text-secondary">Custom Answers</label>
                {customQuestions.map((q) => (
                  <div key={q.id}>
                    <label className="block text-xs text-text-secondary mb-1">{q.label || 'Question'}</label>
                    {q.type === 'text' ? (
                      <TextInput
                        value={customAnswers[q.id] || ''}
                        onChange={(e) => setCustomAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                        placeholder="Answer..."
                      />
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {(q.options || []).map((opt) => {
                          const parseOpts = (a: string, opts: string[]) => {
                            if (!a) return [];
                            if (opts.includes(a)) return [a];
                            if (a.includes('|||')) return a.split('|||').filter(Boolean);
                            return a.split(',').map(s => s.trim()).filter(s => opts.includes(s));
                          };
                          const selected = parseOpts(customAnswers[q.id] || '', q.options || []).includes(opt);
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => {
                                const current = parseOpts(customAnswers[q.id] || '', q.options || []);
                                const next = current.includes(opt) ? current.filter(o => o !== opt) : [...current, opt];
                                setCustomAnswers(prev => ({ ...prev, [q.id]: next.join('|||') }));
                              }}
                              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${selected ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary hover:border-primary/50'}`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {availableTags && (
              <TagSelector
                availableTags={availableTags}
                selectedTagIds={selectedTagIds}
                onChange={setSelectedTagIds}
                label="Tags"
              />
            )}

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Invite</label>
              <Select
                options={invites.map((inv) => ({ value: String(inv.id), label: inv.title }))}
                value={String(selectedInviteId)}
                onChange={(val) => setSelectedInviteId(Number(val))}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">RSVP Status</label>
              <Select
                options={RSVP_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                value={rsvpStatus}
                onChange={(val) => setRsvpStatus(val as RsvpStatus)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Invite Status</label>
              <Select
                options={[
                  { value: 'invited', label: 'Invited' },
                  ...(guest.invite.visibility === 'public' ? [{ value: 'public', label: 'Invited - Public' }] : []),
                  { value: 'not_invited', label: 'Not invited' },
                ]}
                value={inviteStatus}
                onChange={setInviteStatus}
              />
            </div>

            {error && <Alert variant="error">{error}</Alert>}
          </div>

          {/* Footer buttons - pinned */}
          <div className="flex gap-3 px-4 py-3 border-t border-border shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
            >
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function AssignInviteModal({
  invites: allInvites,
  onClose,
  onConfirm,
  isAssigning,
  guestCount,
}: {
  invites: InviteListItem[];
  onClose: () => void;
  onConfirm: (inviteId: number) => void;
  isAssigning: boolean;
  guestCount: number;
}) {
  const invites = useMemo(() => allInvites.filter((i) => !i.is_system), [allInvites]);
  const [selectedInviteId, setSelectedInviteId] = useState<string>(String(invites[0]?.id ?? ''));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-sm bg-background-card rounded-xl border border-border shadow-xl mx-4"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">Assign to Invite</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-background-elevated transition-colors">
            <X size={20} className="text-text-secondary" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-text-secondary">
            Move {guestCount} guest{guestCount !== 1 ? 's' : ''} to:
          </p>

          <Select
            options={invites.map((inv) => ({ value: String(inv.id), label: inv.title }))}
            value={selectedInviteId}
            onChange={setSelectedInviteId}
            placeholder="Select an invite..."
          />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(Number(selectedInviteId))}
              disabled={isAssigning || !selectedInviteId}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
            >
              {isAssigning ? <Loader2 size={18} className="animate-spin" /> : <ArrowRightLeft size={18} />}
              {isAssigning ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function BulkAssignTagModal({
  tags, guestCount, isAssigning, onConfirm, onClose,
}: {
  tags: GuestTag[];
  guestCount: number;
  isAssigning: boolean;
  onConfirm: (tagIds: number[]) => void;
  onClose: () => void;
}) {
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-sm bg-background-card rounded-xl border border-border shadow-xl mx-4"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">Assign Tag</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-background-elevated transition-colors">
            <X size={20} className="text-text-secondary" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-text-secondary">
            Add a tag to {guestCount} guest{guestCount !== 1 ? 's' : ''}:
          </p>
          <TagSelector
            availableTags={tags}
            selectedTagIds={selectedTagIds}
            onChange={setSelectedTagIds}
            label=""
            placeholder="Select a tag..."
          />
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => selectedTagIds.length > 0 && onConfirm(selectedTagIds)}
              disabled={isAssigning || selectedTagIds.length === 0}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
            >
              {isAssigning ? <Loader2 size={18} className="animate-spin" /> : <Tag size={18} />}
              {isAssigning ? 'Assigning...' : 'Assign Tag'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function BulkRsvpModal({
  onClose,
  onConfirm,
  isUpdating,
  guestCount,
}: {
  onClose: () => void;
  onConfirm: (status: RsvpStatus) => void;
  isUpdating: boolean;
  guestCount: number;
}) {
  const [selectedStatus, setSelectedStatus] = useState<RsvpStatus>('yes');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-sm bg-background-card rounded-xl border border-border shadow-xl mx-4"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">Change RSVP Status</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-background-elevated transition-colors">
            <X size={20} className="text-text-secondary" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-text-secondary">
            Update RSVP for {guestCount} guest{guestCount !== 1 ? 's' : ''} to:
          </p>

          <Select
            options={RSVP_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
            value={selectedStatus}
            onChange={(val) => setSelectedStatus(val as RsvpStatus)}
            placeholder="Select status..."
          />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(selectedStatus)}
              disabled={isUpdating}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
            >
              {isUpdating ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
              {isUpdating ? 'Updating...' : 'Update'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}


export function GuestListPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin') ?? false;
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([]);
  const deferredSearch = useDeferredValue(searchQuery);
  const deferredFilters = useDeferredValue(selectedFilters);
  const [initializedFromUrl, setInitializedFromUrl] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [guestToEdit, setGuestToEdit] = useState<GuestWithInvite | null>(null);
  const [guestToRemove, setGuestToRemove] = useState<GuestWithInvite | null>(null);
  const [guestToEmail, setGuestToEmail] = useState<GuestWithInvite | null>(null);

  const { data: invites } = useInvites();
  const { data: billing } = useBillingUsage();
  const { data: allGuests, isLoading } = useAllGuests();
  const { data: rsvpSummary } = useRsvpSummary();
  const { data: guestTags } = useGuestTags();
  const addGuest = useAddInviteGuest();
  const bulkImport = useBulkImportGuests();
  const updateGuest = useUpdateInviteGuest();
  const removeGuest = useRemoveInviteGuest();
  const bulkRemove = useBulkRemoveGuests();
  const sendEmail = useSendGuestEmail();
  const assignGuests = useAssignGuestsToInvite();
  const bulkUpdateRsvp = useBulkUpdateRsvp();
  const createGuestTag = useCreateGuestTag();
  const bulkAssignTag = useBulkAssignTag();
  const [sendingGuestId, setSendingGuestId] = useState<number | null>(null);
  const [showBulkEmailConfirm, setShowBulkEmailConfirm] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState<{ inviteId?: number; headline: string } | null>(null);
  const upgradeGateError: PublishGateError = { message: '', error_code: 'payment_required' };
  const hasPaidAccess = (billing?.subscribed ?? false) || (invites?.some(inv => inv.paid_at !== null) ?? false);
  const [showBulkRemoveConfirm, setShowBulkRemoveConfirm] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [showBulkRsvpModal, setShowBulkRsvpModal] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<number>>(new Set());
  const [reminderGuestIds, setReminderGuestIds] = useState<number[] | null>(null);
  const [nudgeGuestIds, setNudgeGuestIds] = useState<number[] | null>(null);

  const toggleExpanded = useCallback((parentId: number) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  }, []);

  // Build filter categories — RSVP is static, Invite is dynamic
  const filterCategories = useMemo<FilterCategory[]>(() => {
    const categories: FilterCategory[] = [RSVP_FILTER_CATEGORY];
    if (invites && invites.length > 0) {
      categories.push({
        id: 'invite',
        label: 'Invite',
        options: invites.map((inv) => ({ id: String(inv.id), label: inv.title })),
      });
    }
    if (guestTags && guestTags.length > 0) {
      categories.push({
        id: 'tag',
        label: 'Tags',
        options: guestTags.map((tag) => ({ id: String(tag.id), label: tag.name, dotColor: getTagDot(tag.color) })),
      });
    }
    return categories;
  }, [invites, guestTags]);

  // Initialize invite filter from URL param on mount
  useEffect(() => {
    if (initializedFromUrl || !invites) return;
    const inviteParam = searchParams.get('invite');
    if (inviteParam) {
      const inv = invites.find((i) => String(i.id) === inviteParam);
      if (inv) {
        setSelectedFilters((prev) => {
          if (prev.some((f) => f.categoryId === 'invite' && f.optionId === inviteParam)) return prev;
          return [...prev, { categoryId: 'invite', optionId: inviteParam, optionLabel: inv.title, categoryLabel: 'Invite' }];
        });
      }
    }
    setInitializedFromUrl(true);
  }, [invites, searchParams, initializedFromUrl]);

  // Sync URL param when invite filter changes
  useEffect(() => {
    if (!initializedFromUrl) return;
    const inviteFilters = selectedFilters.filter((f) => f.categoryId === 'invite');
    if (inviteFilters.length === 1) {
      setSearchParams({ invite: inviteFilters[0].optionId }, { replace: true });
    } else {
      if (searchParams.has('invite')) {
        setSearchParams({}, { replace: true });
      }
    }
  }, [selectedFilters, initializedFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Single-invite mode derived state
  const singleInviteFilter = useMemo(() => {
    const inviteFilters = selectedFilters.filter((f) => f.categoryId === 'invite');
    return inviteFilters.length === 1 ? inviteFilters[0] : null;
  }, [selectedFilters]);

  const singleInvite = useMemo(() => {
    if (!singleInviteFilter || !invites) return null;
    return invites.find((i) => String(i.id) === singleInviteFilter.optionId) || null;
  }, [singleInviteFilter, invites]);

  const showInviteColumn = !singleInviteFilter;
  const singleInviteIsDraft = singleInvite?.status === 'draft';

  const inviteQuestionsMap = useMemo(() => {
    const map = new Map<number, CustomQuestion[]>();
    for (const inv of invites ?? []) {
      if (inv.rsvp_content?.customQuestions?.length) {
        map.set(inv.id, inv.rsvp_content.customQuestions);
      }
    }
    return map;
  }, [invites]);

  // Client-side filtering (matches InvitesListPage pattern)
  const guests = useMemo(() => {
    if (!allGuests) return [];
    let result = allGuests;

    if (deferredSearch.trim()) {
      const query = deferredSearch.toLowerCase();
      result = result.filter((g) =>
        g.name.toLowerCase().includes(query) ||
        g.email?.toLowerCase().includes(query) ||
        g.invite.title.toLowerCase().includes(query)
      );
    }

    const rsvpStatuses = deferredFilters
      .filter((f) => f.categoryId === 'rsvp')
      .map((f) => f.optionId);
    if (rsvpStatuses.length > 0) {
      result = result.filter((g) => rsvpStatuses.includes(g.rsvp_status));
    }

    const inviteFilters = deferredFilters.filter((f) => f.categoryId === 'invite');
    if (inviteFilters.length > 0) {
      const inviteIds = inviteFilters.map((f) => Number(f.optionId));
      result = result.filter((g) => inviteIds.includes(g.invite.id));
    }

    const tagFilters = deferredFilters.filter((f) => f.categoryId === 'tag');
    if (tagFilters.length > 0) {
      const tagIds = tagFilters.map((f) => Number(f.optionId));
      result = result.filter((g) =>
        g.tags && g.tags.some((t) => tagIds.includes(t.id))
      );
    }

    return result;
  }, [allGuests, deferredSearch, deferredFilters]);

  // Separate lead guests from plus-one children
  const { leadGuests, childrenByParent, orphanPlusOnes } = useMemo(() => {
    const leads: GuestWithInvite[] = [];
    const byParent = new Map<number, GuestWithInvite[]>();
    for (const g of guests) {
      if (g.parent_guest_id) {
        const list = byParent.get(g.parent_guest_id) || [];
        list.push(g);
        byParent.set(g.parent_guest_id, list);
      } else {
        leads.push(g);
      }
    }
    const leadIdSet = new Set(leads.map(l => l.id));
    const orphans: GuestWithInvite[] = [];
    for (const [parentId, children] of byParent) {
      if (!leadIdSet.has(parentId)) {
        orphans.push(...children);
      }
    }
    return { leadGuests: leads, childrenByParent: byParent, orphanPlusOnes: orphans };
  }, [guests]);

  // Only include visible guests in selection (leads + expanded children + orphans)
  const guestIds = useMemo(() => {
    const ids: number[] = [];
    for (const lead of leadGuests) {
      ids.push(lead.id);
      if (expandedParents.has(lead.id)) {
        const children = childrenByParent.get(lead.id);
        if (children) ids.push(...children.map(c => c.id));
      }
    }
    ids.push(...orphanPlusOnes.map(g => g.id));
    return ids;
  }, [leadGuests, childrenByParent, expandedParents, orphanPlusOnes]);
  const selection = useSelection(guestIds);

  const selectedWithEmail = useMemo(() => {
    return guests.filter((g) => selection.isSelected(g.id) && g.email && !g.unsubscribed_at).length;
  }, [guests, selection]);

  const skippedBreakdown = useMemo(() => {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    const selected = guests.filter((g) => selection.isSelected(g.id));
    return {
      noEmail: selected.filter((g) => !g.email).length,
      unsubscribed: selected.filter((g) => g.email && g.unsubscribed_at).length,
      draft: selected.filter((g) => g.email && !g.unsubscribed_at && g.invite.status === 'draft').length,
      sentToday: selected.filter((g) => g.email && !g.unsubscribed_at && g.invite.status !== 'draft' && g.email_sent_at && new Date(g.email_sent_at) > today).length,
    };
  }, [guests, selection]);

  const selectedNotInvited = useMemo(() => {
    return guests.filter((g) => selection.isSelected(g.id) && !g.email_sent_at && !(g.invite.visibility === 'public' && g.rsvp_submitted_at)).length;
  }, [guests, selection]);

  const eligibleForEmail = useMemo(() => {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    return guests.filter((g) => {
      if (!selection.isSelected(g.id) || !g.email) return false;
      if (g.unsubscribed_at) return false; // Skip unsubscribed guests
      if (isAdmin) return true;
      if (g.invite.status === 'draft') return false;
      if (g.email_sent_at && new Date(g.email_sent_at) > today) return false;
      return true;
    });
  }, [guests, selection, isAdmin]);

  const skippedEmailCount = selectedWithEmail - eligibleForEmail.length;

  const handleBulkSendEmail = async () => {
    let sent = 0;
    let failed = 0;
    let upgradeInviteId: number | null = null;
    for (const guest of eligibleForEmail) {
      try {
        await sendEmail.mutateAsync({ inviteId: guest.invite.id, guestId: guest.id });
        sent++;
      } catch (err: any) {
        if (err.response?.data?.error_code === 'subscription_required') {
          upgradeInviteId = guest.invite.id;
          break;
        }
        failed++;
      }
    }
    if (upgradeInviteId) {
      setUpgradeModal({ inviteId: upgradeInviteId, headline: 'Unlock Email Sending' });
    } else if (sent > 0 && failed === 0) {
      showToast(`${sent} email${sent !== 1 ? 's' : ''} queued`, 'success');
    } else if (sent > 0 && failed > 0) {
      showToast(`${sent} email${sent !== 1 ? 's' : ''} queued, ${failed} failed`, 'warning');
    } else {
      showToast('No emails were sent', 'error');
    }
    selection.clear();
    setShowBulkEmailConfirm(false);
  };

  const handleBulkRemove = async () => {
    const selectedGuests = guests.filter((g) => selection.isSelected(g.id));
    // Group by invite_id to use bulk-remove endpoint
    const byInvite = new Map<number, number[]>();
    for (const guest of selectedGuests) {
      const list = byInvite.get(guest.invite.id) || [];
      list.push(guest.id);
      byInvite.set(guest.invite.id, list);
    }
    let totalRemoved = 0;
    for (const [inviteId, guestIds] of byInvite) {
      try {
        const result = await bulkRemove.mutateAsync({ inviteId, guestIds });
        totalRemoved += result.count;
      } catch {
        // continue with remaining
      }
    }
    showToast(`${totalRemoved} guest(s) removed`, 'success');
    selection.clear();
    setShowBulkRemoveConfirm(false);
  };

  const handleBulkAssign = async (inviteId: number) => {
    const selectedIds = guests.filter((g) => selection.isSelected(g.id)).map((g) => g.id);
    try {
      await assignGuests.mutateAsync({ guestIds: selectedIds, inviteId });
      const targetInvite = invites?.find((i) => i.id === inviteId);
      showToast(`${selectedIds.length} guest${selectedIds.length !== 1 ? 's' : ''} assigned to ${targetInvite?.title ?? 'invite'}`, 'success');
      selection.clear();
      setShowBulkAssignModal(false);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to assign guests', 'error');
    }
  };

  const handleBulkAssignTag = async (tagIds: number[]) => {
    const selectedIds = guests.filter((g) => selection.isSelected(g.id)).map((g) => g.id);
    try {
      await Promise.all(tagIds.map((tagId) => bulkAssignTag.mutateAsync({ tagId, guestIds: selectedIds })));
      showToast(`${tagIds.length > 1 ? `${tagIds.length} tags` : `Tag "${guestTags?.find(t => t.id === tagIds[0])?.name}"`} added to ${selectedIds.length} guest${selectedIds.length !== 1 ? 's' : ''}`, 'success');
      selection.clear();
      setShowBulkTagModal(false);
    } catch {
      showToast('Failed to assign tag', 'error');
    }
  };

  const handleBulkRsvpChange = async (status: RsvpStatus) => {
    const selectedIds = guests.filter((g) => selection.isSelected(g.id)).map((g) => g.id);
    try {
      const result = await bulkUpdateRsvp.mutateAsync({ guestIds: selectedIds, rsvpStatus: status });
      const label = RSVP_OPTIONS.find(o => o.value === status)?.label ?? status;
      showToast(`${result.count} guest${result.count !== 1 ? 's' : ''} updated to ${label}`, 'success');
      selection.clear();
      setShowBulkRsvpModal(false);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to update RSVP', 'error');
    }
  };

  const handleStatsMarkAsInvited = async (guestIds: number[]) => {
    const targets = guests.filter((g) => guestIds.includes(g.id) && g.invite.status !== 'draft');
    let updated = 0;
    for (const guest of targets) {
      try {
        await updateGuest.mutateAsync({
          inviteId: guest.invite.id,
          guestId: guest.id,
          data: { mark_as_invited: true },
        });
        updated++;
      } catch {
        // continue
      }
    }
    showToast(`${updated} guest${updated !== 1 ? 's' : ''} marked as invited`, 'success');
  };

  const handleBulkMarkAsInvited = async () => {
    const uninvited = guests.filter((g) => selection.isSelected(g.id) && !g.email_sent_at && !g.marked_as_invited_at && g.invite.status !== 'draft' && !(g.invite.visibility === 'public' && g.rsvp_submitted_at));
    let updated = 0;
    for (const guest of uninvited) {
      try {
        await updateGuest.mutateAsync({
          inviteId: guest.invite.id,
          guestId: guest.id,
          data: { mark_as_invited: true },
        });
        updated++;
      } catch {
        // continue with remaining
      }
    }
    showToast(`${updated} guest${updated !== 1 ? 's' : ''} marked as invited`, 'success');
    selection.clear();
  };

  const handleFilterToggle = useCallback(
    (categoryId: string, optionId: string, optionLabel: string, categoryLabel: string) => {
      setSelectedFilters((prev) => {
        const exists = prev.some((f) => f.categoryId === categoryId && f.optionId === optionId);
        if (exists) {
          return prev.filter((f) => !(f.categoryId === categoryId && f.optionId === optionId));
        }
        return [...prev, { categoryId, optionId, optionLabel, categoryLabel }];
      });
    },
    []
  );

  const handleFilterRemove = useCallback((categoryId: string, optionId: string) => {
    setSelectedFilters((prev) =>
      prev.filter((f) => !(f.categoryId === categoryId && f.optionId === optionId))
    );
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedFilters([]);
  }, []);

  const handleExportCsv = useCallback(() => {
    if (!guests.length) return;
    if (!hasPaidAccess) {
      setUpgradeModal({ inviteId: invites?.[0]?.id, headline: 'Unlock Import & Export' });
      return;
    }
    const escapeCsv = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Collect all custom questions across the exported invites, preserving order and deduplicating by id
    const seenQuestionIds = new Set<string>();
    const allCustomQuestions: { id: string; label: string }[] = [];
    for (const g of guests) {
      const qs = inviteQuestionsMap.get(g.invite_id) ?? [];
      for (const q of qs) {
        if (!seenQuestionIds.has(q.id)) {
          seenQuestionIds.add(q.id);
          allCustomQuestions.push({ id: q.id, label: q.label });
        }
      }
    }

    const headers = ['Name', 'Email', 'Invite', 'RSVP', 'Dietary Preference', 'Allergies', 'Dietary Notes', 'Tags', 'Plus One Of', 'Views', 'Invite Status', 'RSVP Date', ...allCustomQuestions.map(q => q.label)];

    const rows = guests.map(g => [
      g.name || '',
      g.email || '',
      g.invite.title || '',
      RSVP_OPTIONS.find(o => o.value === g.rsvp_status)?.label || g.rsvp_status,
      g.dietary_preference || '',
      (Array.isArray(g.allergies) ? g.allergies.join('; ') : '') || '',
      g.dietary_notes || '',
      g.tags?.map(t => t.name).join('; ') || '',
      g.parent_guest_name || '',
      String(g.view_count),
      g.email_sent_at
        ? `Invited ${new Date(g.email_sent_at).toLocaleDateString()}`
        : g.invite.visibility === 'public' && g.rsvp_submitted_at
          ? 'Invited - Public'
          : 'Not invited',
      g.rsvp_submitted_at ? new Date(g.rsvp_submitted_at).toLocaleDateString() : '',
      ...allCustomQuestions.map(q => g.custom_answers?.[q.id] || ''),
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `guests-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${guests.length} guest${guests.length !== 1 ? 's' : ''}`, 'success');
  }, [guests, hasPaidAccess, invites, inviteQuestionsMap, setUpgradeModal, showToast]);

  const handleInviteClick = useCallback((guest: GuestWithInvite) => {
    const inviteId = String(guest.invite.id);
    setSelectedFilters((prev) => {
      // Remove any existing invite filters, add this one
      const withoutInvite = prev.filter((f) => f.categoryId !== 'invite');
      return [...withoutInvite, { categoryId: 'invite', optionId: inviteId, optionLabel: guest.invite.title, categoryLabel: 'Invite' }];
    });
  }, []);

  // Options for the add guest modal — omit selector when single invite is active
  const addGuestInviteOptions = useMemo(() => {
    if (singleInviteFilter) return undefined; // hide selector in single-invite mode
    if (!invites) return [];
    return invites.filter((inv) => !inv.is_system).map((inv) => ({ value: String(inv.id), label: inv.title }));
  }, [invites, singleInviteFilter]);

  const handleInlineRsvpChange = async (guest: GuestWithInvite, status: RsvpStatus) => {
    try {
      await updateGuest.mutateAsync({
        inviteId: guest.invite.id,
        guestId: guest.id,
        data: { rsvp_status: status },
      });
      showToast(`RSVP updated to ${RSVP_OPTIONS.find(o => o.value === status)?.label}`, 'success');
    } catch {
      showToast('Failed to update RSVP', 'error');
    }
  };

  const handleMarkAsInvited = async (guest: GuestWithInvite, invited: boolean) => {
    try {
      await updateGuest.mutateAsync({
        inviteId: guest.invite.id,
        guestId: guest.id,
        data: { mark_as_invited: invited },
      });
      showToast(invited ? `${guest.name} marked as invited` : `${guest.name} marked as not invited`, 'success');
    } catch {
      showToast('Failed to update invite status', 'error');
    }
  };

  const handleCopyLink = (guest: GuestWithInvite) => {
    navigator.clipboard.writeText(guest.public_url).catch(() => {
      const el = document.createElement('textarea');
      el.value = guest.public_url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    showToast('Link copied to clipboard', 'success');
  };

  const handleWhatsApp = (guest: GuestWithInvite) => {
    const text = encodeURIComponent(
      `You're invited to ${guest.invite.title}! View the invitation and RSVP here: ${guest.public_url}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleEditGuest = async ({ name, email, inviteId, dietaryPreference, allergies, rsvpStatus, markAsInvited, tagIds, customAnswers }: { name: string; email: string; inviteId: number; dietaryPreference: string; allergies: string[]; rsvpStatus: RsvpStatus; markAsInvited?: boolean; tagIds?: number[]; customAnswers?: Record<string, string> }) => {
    if (!guestToEdit) return;

    // If invite changed, reassign first
    if (inviteId !== guestToEdit.invite.id) {
      await assignGuests.mutateAsync({ guestIds: [guestToEdit.id], inviteId });
    }

    // Build update data — include rsvp_status if changed, mark_as_invited if changed
    const data: Record<string, unknown> = {
      name,
      email: email || undefined,
      dietary_preference: dietaryPreference || undefined,
      allergies: allergies.length > 0 ? allergies : undefined,
    };
    if (rsvpStatus !== guestToEdit.rsvp_status) {
      data.rsvp_status = rsvpStatus;
    }
    if (markAsInvited !== undefined) {
      data.mark_as_invited = markAsInvited;
    }
    if (tagIds !== undefined) {
      data.tag_ids = tagIds;
    }
    if (customAnswers !== undefined) {
      data.custom_answers = customAnswers;
    }

    await updateGuest.mutateAsync({
      inviteId,
      guestId: guestToEdit.id,
      data,
    });
    showToast('Guest updated', 'success');
  };

  const handleConfirmRemove = async () => {
    if (!guestToRemove) return;
    try {
      await removeGuest.mutateAsync({ inviteId: guestToRemove.invite.id, guestId: guestToRemove.id });
      showToast('Guest removed', 'success');
    } catch {
      showToast('Failed to remove guest', 'error');
    } finally {
      setGuestToRemove(null);
    }
  };

  const handleConfirmSendEmail = async () => {
    if (!guestToEmail) return;
    setSendingGuestId(guestToEmail.id);
    try {
      await sendEmail.mutateAsync({ inviteId: guestToEmail.invite.id, guestId: guestToEmail.id });
      showToast(`Email queued for ${guestToEmail.name}`, 'success');
    } catch (err: any) {
      if (err.response?.data?.error_code === 'subscription_required') {
        setUpgradeModal({ inviteId: guestToEmail.invite.id, headline: 'Unlock Email Sending' });
      } else {
        showToast(err.response?.data?.message || 'Failed to send email', 'error');
      }
    } finally {
      setSendingGuestId(null);
      setGuestToEmail(null);
    }
  };

  const handleAddGuest = async ({ inviteId, name, email, tagIds }: { inviteId?: number; name: string; email: string; tagIds?: number[] }) => {
    const resolvedId = inviteId ?? (singleInviteFilter ? Number(singleInviteFilter.optionId) : undefined);
    if (!resolvedId) return;
    try {
      await addGuest.mutateAsync({
        inviteId: resolvedId,
        data: { name, email: email || undefined, tag_ids: tagIds },
      });
      showToast('Guest added successfully', 'success');
    } catch (err: any) {
      if (err.response?.data?.error_code === 'guest_limit_reached') {
        setUpgradeModal({ inviteId: resolvedId, headline: 'Unlock Full Guest Management' });
      } else {
        throw err;
      }
    }
  };

  const handleBulkImport = async ({ inviteId, guests: guestData }: { inviteId?: number; guests: { name: string; email?: string; tag_ids?: number[]; dietary_preference?: string; allergies?: string[]; rsvp_status?: string }[] }) => {
    const resolvedId = inviteId ?? (singleInviteFilter ? Number(singleInviteFilter.optionId) : undefined);
    if (!resolvedId) return;
    try {
      await bulkImport.mutateAsync({
        inviteId: resolvedId,
        data: { guests: guestData },
      });
      showToast(`${guestData.length} guests imported`, 'success');
    } catch (err: any) {
      if (err.response?.data?.error_code === 'guest_limit_reached') {
        setUpgradeModal({ inviteId: resolvedId, headline: 'Unlock Full Guest Management' });
      } else {
        throw err;
      }
    }
  };

  const handleCreateTag = async (name: string) => {
    return await createGuestTag.mutateAsync({ name, color: 'primary' });
  };

  const hasFilters = searchQuery.trim() || selectedFilters.length > 0;

  // Filtered RSVP totals — recompute from per_invite data respecting active filters
  const filteredRsvpTotals = useMemo(() => {
    if (!rsvpSummary) return null;

    const inviteFilterIds = deferredFilters
      .filter((f) => f.categoryId === 'invite')
      .map((f) => Number(f.optionId));
    const rsvpFilterStatuses = deferredFilters
      .filter((f) => f.categoryId === 'rsvp')
      .map((f) => f.optionId);

    let entries = rsvpSummary.per_invite;
    if (inviteFilterIds.length > 0) {
      entries = entries.filter((inv) => inviteFilterIds.includes(inv.invite_id));
    }

    const base = inviteFilterIds.length > 0
      ? entries.reduce(
          (acc, inv) => ({
            total_guests: acc.total_guests + inv.total,
            total_invited: acc.total_invited + inv.invited,
            attending: acc.attending + inv.yes,
            not_attending: acc.not_attending + inv.no,
            maybe: acc.maybe + inv.maybe,
            pending: acc.pending + inv.pending,
            total_plus_ones: acc.total_plus_ones + inv.plus_ones,
          }),
          { total_guests: 0, total_invited: 0, attending: 0, not_attending: 0, maybe: 0, pending: 0, total_plus_ones: 0 }
        )
      : rsvpSummary.totals;

    if (rsvpFilterStatuses.length > 0) {
      const activeSet = new Set(rsvpFilterStatuses);
      return {
        total_guests: base.total_guests,
        total_invited: base.total_invited,
        attending: activeSet.has('yes') ? base.attending : 0,
        not_attending: activeSet.has('no') ? base.not_attending : 0,
        maybe: activeSet.has('maybe') ? base.maybe : 0,
        pending: activeSet.has('pending') ? base.pending : 0,
        total_plus_ones: activeSet.has('yes') ? base.total_plus_ones : 0,
      };
    }

    return base;
  }, [rsvpSummary, deferredFilters]);

  // Filtered recent responses
  const filteredRecentResponses = useMemo(() => {
    if (!rsvpSummary) return [];
    let responses = rsvpSummary.recent_responses;

    const inviteFilterIds = deferredFilters
      .filter((f) => f.categoryId === 'invite')
      .map((f) => Number(f.optionId));
    const rsvpFilterStatuses = deferredFilters
      .filter((f) => f.categoryId === 'rsvp')
      .map((f) => f.optionId);

    if (inviteFilterIds.length > 0) {
      responses = responses.filter((r) => inviteFilterIds.includes(r.invite_id));
    }
    if (rsvpFilterStatuses.length > 0) {
      responses = responses.filter((r) => rsvpFilterStatuses.includes(r.rsvp_status));
    }
    if (deferredSearch.trim()) {
      const q = deferredSearch.toLowerCase();
      responses = responses.filter(
        (r) => r.name.toLowerCase().includes(q) || r.invite_title.toLowerCase().includes(q)
      );
    }
    return responses;
  }, [rsvpSummary, deferredFilters, deferredSearch]);

  // Bulk action handler for reminder/nudge — applies same eligibility rules as bulk email
  const handleConfirmBulkAction = async (guestIds: number[]) => {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    const targetGuests = guests.filter((g) => {
      if (!guestIds.includes(g.id) || !g.email) return false;
      if (isAdmin) return true;
      if (g.invite.status === 'draft') return false;
      if (g.email_sent_at && new Date(g.email_sent_at) > today) return false;
      return true;
    });
    let sent = 0;
    let failed = 0;
    for (const guest of targetGuests) {
      try {
        await sendEmail.mutateAsync({ inviteId: guest.invite.id, guestId: guest.id });
        sent++;
      } catch {
        failed++;
      }
    }
    if (sent > 0 && failed === 0) {
      showToast(`${sent} email${sent !== 1 ? 's' : ''} queued`, 'success');
    } else if (sent > 0 && failed > 0) {
      showToast(`${sent} email${sent !== 1 ? 's' : ''} queued, ${failed} failed`, 'warning');
    } else {
      showToast('No emails were sent', 'error');
    }
    setReminderGuestIds(null);
    setNudgeGuestIds(null);
  };

  const guestsWithAllergies = useMemo(
    () => (allGuests ?? []).filter(g => g.allergies && g.allergies.length > 0),
    [allGuests]
  );

  const [showAllergyPopup, setShowAllergyPopup] = useState(false);

  useEffect(() => {
    if (guestsWithAllergies.length === 0) return;
    const hasPastEvent = invites?.some(inv => inv.event_date && new Date(inv.event_date) < new Date());
    if (!hasPastEvent) return;
    if (localStorage.getItem('allergy_popup_shown')) return;
    setShowAllergyPopup(true);
  }, [guestsWithAllergies.length, invites]);

  return (
    <div className="min-h-screen flex flex-col">
      <TopHeader title="Guests" />

      {/* Allergy alert banner */}
      {guestsWithAllergies.length > 0 && (
        <div className="px-4 sm:px-8 py-3 flex items-center gap-3 bg-error/5 border-b border-error/20">
          <AlertTriangle size={16} className="text-error flex-shrink-0" />
          <p className="text-sm text-error">
            <strong>{guestsWithAllergies.length} guest{guestsWithAllergies.length !== 1 ? 's' : ''}</strong> {guestsWithAllergies.length !== 1 ? 'have' : 'has'} reported allergies — please confirm directly with each guest and your venue before and on the day. <Link to="/app/guests/allergies" className="font-medium underline underline-offset-2 whitespace-nowrap">Manage data</Link>
          </p>
        </div>
      )}

      {/* Search, filters and actions bar */}
      <div className="sticky top-0 z-40 px-4 sm:px-8 pt-3 pb-2 border-b border-border bg-background-card">
        <BrowseFilters
          categories={filterCategories}
          selectedFilters={selectedFilters}
          onFilterToggle={handleFilterToggle}
          onFilterRemove={handleFilterRemove}
          onClearAll={handleClearFilters}
          searchQuery={searchQuery}
          onSearchChange={(v) => { setSearchQuery(v); }}
          searchPlaceholder="Search guests..."
          actions={invites && invites.length > 0 ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (!hasPaidAccess) {
                    setUpgradeModal({ inviteId: invites?.[0]?.id, headline: 'Unlock Import & Export' });
                    return;
                  }
                  setShowImportModal(true);
                }}
                icon={<ArrowRightLeft size={16} />}
                className="!px-2 !py-2.5 !rounded-lg sm:px-4 sm:py-2 sm:rounded-full"
                data-builder="guests-import-export-btn"
              >
                <span className="hidden sm:inline">Import / Export</span>
              </Button>
              {(() => {
                const filteredInviteId = searchParams.get('invite');
                const emailableInvites = invites?.filter(i => i.status === 'published') ?? [];
                const directInviteId = filteredInviteId ?? (emailableInvites.length === 1 ? String(emailableInvites[0].id) : null);
                return directInviteId ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/app/invites/${directInviteId}/emails`)}
                    icon={<Mail size={16} />}
                    className="!px-2 !py-2.5 !rounded-lg sm:px-4 sm:py-2 sm:rounded-full"
                  >
                    <span className="hidden sm:inline">Email guests</span>
                  </Button>
                ) : emailableInvites.length > 0 ? (
                  <Dropdown>
                    <DropdownTrigger asChild>
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Mail size={16} />}
                        className="!px-2 !py-2.5 !rounded-lg sm:px-4 sm:py-2 sm:rounded-full"
                      >
                        <span className="hidden sm:inline">Email guests</span>
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu align="right">
                      {emailableInvites.map(invite => (
                        <DropdownItem key={invite.id} onClick={() => navigate(`/app/invites/${invite.id}/emails`)}>
                          {invite.title}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                ) : null;
              })()}
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddModal(true)}
                icon={<Plus size={16} />}
                className="!px-2 !py-2.5 !rounded-lg sm:px-4 sm:py-2 sm:rounded-full"
                data-builder="guests-add-btn"
              >
                <span className="hidden sm:inline">Add Guest</span>
              </Button>
            </>
          ) : undefined}
        />
      </div>

      {/* Content */}
      <div className="flex-1 px-4 sm:px-8 py-6 space-y-6">
        {/* RSVP Stats Section */}
        {allGuests && allGuests.length > 0 && filteredRsvpTotals && (
          <RsvpStatsSection
            totals={filteredRsvpTotals}
            recentResponses={filteredRecentResponses}
            guests={guests}
            onSendReminder={(ids) => setReminderGuestIds(ids)}
            onNudge={(ids) => setNudgeGuestIds(ids)}
            onSendToUninvited={(ids) => setReminderGuestIds(ids)}
            onMarkAsInvited={handleStatsMarkAsInvited}
            isSending={sendEmail.isPending || updateGuest.isPending}
            isAdmin={isAdmin}
            showInviteColumn={showInviteColumn}
          />
        )}

        {/* Guest Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-primary" />
          </div>
        ) : !allGuests?.length && !hasFilters ? (
          <EmptyState
            icon={<Users size={48} />}
            title="No guests yet"
            description="Add guests to your invites to see them here."
            action={
              invites && invites.length > 0 ? (
                <Button
                  variant="primary"
                  onClick={() => setShowAddModal(true)}
                  icon={<Plus size={18} />}
                >
                  Add Guest
                </Button>
              ) : (
                <Link
                  to="/app/invites"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-text-inverse rounded-lg hover:bg-primary-hover transition-colors"
                >
                  <ExternalLink size={18} />
                  Go to Invites
                </Link>
              )
            }
          />
        ) : guests.length === 0 && hasFilters ? (
          <EmptyState
            icon={<Mail size={48} />}
            title="No matching guests"
            description="Try adjusting your search or filters"
          />
        ) : (
          <div className="bg-background-card rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 !pr-0">
                    <TableCheckbox
                      checked={selection.isAllSelected}
                      indeterminate={selection.isPartiallySelected}
                      onChange={selection.toggleAll}
                    />
                  </TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead>RSVP</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Plus</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="sticky right-0 bg-background-card shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leadGuests.map((guest) => {
                  const children = childrenByParent.get(guest.id);
                  const plusOneCount = children?.length || 0;
                  const isExpanded = expandedParents.has(guest.id);
                  return (
                    <Fragment key={guest.id}>
                      <GuestRow
                        guest={guest}
                        selected={selection.isSelected(guest.id)}
                        onToggle={() => selection.toggle(guest.id)}
                        onCopyLink={() => handleCopyLink(guest)}
                        onWhatsApp={() => handleWhatsApp(guest)}
                        onEdit={() => setGuestToEdit(guest)}
                        onRemove={() => setGuestToRemove(guest)}
                        onSendEmail={() => setGuestToEmail(guest)}
                        onRsvpChange={(status) => handleInlineRsvpChange(guest, status)}
                        onMarkAsInvited={(invited) => handleMarkAsInvited(guest, invited)}
                        isSending={sendingGuestId === guest.id}
                        showInviteColumn={showInviteColumn}
                        onInviteClick={() => handleInviteClick(guest)}
                        isAdmin={isAdmin}
                        plusOneCount={plusOneCount}
                        isExpanded={isExpanded}
                        onToggleExpand={() => toggleExpanded(guest.id)}
                        customQuestions={inviteQuestionsMap.get(guest.invite_id)}
                      />
                      {isExpanded && children?.map((child) => (
                        <GuestRow
                          key={child.id}
                          guest={child}
                          selected={selection.isSelected(child.id)}
                          onToggle={() => selection.toggle(child.id)}
                          onCopyLink={() => handleCopyLink(child)}
                          onWhatsApp={() => handleWhatsApp(child)}
                          onEdit={() => setGuestToEdit(child)}
                          onRemove={() => setGuestToRemove(child)}
                          onSendEmail={() => setGuestToEmail(child)}
                          onRsvpChange={(status) => handleInlineRsvpChange(child, status)}
                          onMarkAsInvited={(invited) => handleMarkAsInvited(child, invited)}
                          isSending={sendingGuestId === child.id}
                          showInviteColumn={showInviteColumn}
                          onInviteClick={() => handleInviteClick(child)}
                          isAdmin={isAdmin}
                          isIndented
                          customQuestions={inviteQuestionsMap.get(child.invite_id)}
                        />
                      ))}
                    </Fragment>
                  );
                })}
                {orphanPlusOnes.map((guest) => (
                  <GuestRow
                    key={guest.id}
                    guest={guest}
                    selected={selection.isSelected(guest.id)}
                    onToggle={() => selection.toggle(guest.id)}
                    onCopyLink={() => handleCopyLink(guest)}
                    onWhatsApp={() => handleWhatsApp(guest)}
                    onEdit={() => setGuestToEdit(guest)}
                    onRemove={() => setGuestToRemove(guest)}
                    onSendEmail={() => setGuestToEmail(guest)}
                    onRsvpChange={(status) => handleInlineRsvpChange(guest, status)}
                    onMarkAsInvited={(invited) => handleMarkAsInvited(guest, invited)}
                    isSending={sendingGuestId === guest.id}
                    showInviteColumn={showInviteColumn}
                    onInviteClick={() => handleInviteClick(guest)}
                    isAdmin={isAdmin}
                    isIndented
                    customQuestions={inviteQuestionsMap.get(guest.invite_id)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showAddModal && (
          <AddGuestModal
            onClose={() => setShowAddModal(false)}
            inviteOptions={addGuestInviteOptions}
            tags={guestTags}
            onAdd={handleAddGuest}
            isAdding={addGuest.isPending}
          />
        )}
        {showImportModal && (
          <BulkImportModal
            onClose={() => setShowImportModal(false)}
            inviteOptions={addGuestInviteOptions}
            onImport={handleBulkImport}
            isImporting={bulkImport.isPending}
            tags={guestTags}
            onCreateTag={handleCreateTag}
            onExport={guests.length > 0 ? handleExportCsv : undefined}
            exportGuestCount={guests.length}
          />
        )}
      </AnimatePresence>

      {/* Edit Guest Modal */}
      <AnimatePresence>
        {guestToEdit && invites && (
          <EditGuestModal
            guest={guestToEdit}
            invites={invites}
            tags={guestTags}
            customQuestions={inviteQuestionsMap.get(guestToEdit.invite_id)}
            onClose={() => setGuestToEdit(null)}
            onSave={handleEditGuest}
            isSaving={updateGuest.isPending || assignGuests.isPending}
          />
        )}
      </AnimatePresence>

      {/* Remove Guest Confirmation */}
      <ConfirmModal
        isOpen={!!guestToRemove}
        title="Remove Guest"
        message={guestToRemove ? `Remove ${guestToRemove.name} from ${guestToRemove.invite.title}? This action cannot be undone.` : ''}
        confirmText="Remove"
        confirmVariant="danger"
        onConfirm={handleConfirmRemove}
        onClose={() => setGuestToRemove(null)}
        isLoading={removeGuest.isPending}
      />

      {/* Send Email Confirmation */}
      <ConfirmModal
        isOpen={!!guestToEmail}
        title={guestToEmail?.email_sent_at ? 'Resend Invite Email' : 'Send Invite Email'}
        message={guestToEmail ? `${guestToEmail.email_sent_at ? 'Resend' : 'Send'} invite email to ${guestToEmail.name} (${guestToEmail.email})?` : ''}
        confirmText={guestToEmail?.email_sent_at ? 'Resend' : 'Send'}
        confirmVariant="primary"
        onConfirm={handleConfirmSendEmail}
        onClose={() => setGuestToEmail(null)}
        isLoading={sendEmail.isPending}
      />

      {/* Bulk Email Confirmation */}
      <ConfirmModal
        isOpen={showBulkEmailConfirm}
        title="Send Invite Emails"
        message={
          eligibleForEmail.length === 0
            ? `None of the selected guests can receive emails right now. `
              + [
                  skippedBreakdown.noEmail > 0 && `${skippedBreakdown.noEmail} have no email address`,
                  skippedBreakdown.unsubscribed > 0 && `${skippedBreakdown.unsubscribed} unsubscribed`,
                  skippedBreakdown.draft > 0 && `${skippedBreakdown.draft} on a draft invite`,
                  skippedBreakdown.sentToday > 0 && `${skippedBreakdown.sentToday} already emailed today`,
                ].filter(Boolean).join(', ') + '.'
            : `Send invite emails to ${eligibleForEmail.length} guest${eligibleForEmail.length !== 1 ? 's' : ''}?`
              + (skippedEmailCount > 0
                ? ` ${skippedEmailCount} skipped — `
                  + [
                      skippedBreakdown.noEmail > 0 && `${skippedBreakdown.noEmail} no email`,
                      skippedBreakdown.unsubscribed > 0 && `${skippedBreakdown.unsubscribed} unsubscribed`,
                      skippedBreakdown.draft > 0 && `${skippedBreakdown.draft} draft invite`,
                      skippedBreakdown.sentToday > 0 && `${skippedBreakdown.sentToday} already emailed today`,
                    ].filter(Boolean).join(', ') + '.'
                : '')
        }
        confirmText={eligibleForEmail.length === 0 ? 'OK' : 'Send'}
        confirmVariant="primary"
        onConfirm={eligibleForEmail.length === 0 ? () => setShowBulkEmailConfirm(false) : handleBulkSendEmail}
        onClose={() => setShowBulkEmailConfirm(false)}
        isLoading={sendEmail.isPending}
      />

      {/* Bulk Remove Confirmation */}
      <ConfirmModal
        isOpen={showBulkRemoveConfirm}
        title="Remove Guests"
        message={`Remove ${selection.count} selected guest${selection.count !== 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText="Remove"
        confirmVariant="danger"
        onConfirm={handleBulkRemove}
        onClose={() => setShowBulkRemoveConfirm(false)}
        isLoading={bulkRemove.isPending}
      />

      {/* Bulk Assign Tag Modal */}
      <AnimatePresence>
        {showBulkTagModal && guestTags && guestTags.length > 0 && (
          <BulkAssignTagModal
            tags={guestTags}
            guestCount={selection.count}
            isAssigning={bulkAssignTag.isPending}
            onConfirm={handleBulkAssignTag}
            onClose={() => setShowBulkTagModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Bulk Assign Modal */}
      <AnimatePresence>
        {showBulkAssignModal && invites && (
          <AssignInviteModal
            invites={invites}
            onClose={() => setShowBulkAssignModal(false)}
            onConfirm={handleBulkAssign}
            isAssigning={assignGuests.isPending}
            guestCount={selection.count}
          />
        )}
        {showBulkRsvpModal && (
          <BulkRsvpModal
            onClose={() => setShowBulkRsvpModal(false)}
            onConfirm={handleBulkRsvpChange}
            isUpdating={bulkUpdateRsvp.isPending}
            guestCount={selection.count}
          />
        )}
      </AnimatePresence>

      {/* Send Reminder Confirmation */}
      <ConfirmModal
        isOpen={!!reminderGuestIds}
        title="Send Reminder Emails"
        message={reminderGuestIds ? `Send reminder emails to ${reminderGuestIds.length} guest${reminderGuestIds.length !== 1 ? 's' : ''} who haven't replied?` : ''}
        confirmText="Send"
        confirmVariant="primary"
        onConfirm={() => reminderGuestIds && handleConfirmBulkAction(reminderGuestIds)}
        onClose={() => setReminderGuestIds(null)}
        isLoading={sendEmail.isPending}
      />

      {/* Nudge Guests Confirmation */}
      <ConfirmModal
        isOpen={!!nudgeGuestIds}
        title="Nudge Guests"
        message={nudgeGuestIds ? `Send a nudge email to ${nudgeGuestIds.length} guest${nudgeGuestIds.length !== 1 ? 's' : ''} who viewed but haven't RSVP'd?` : ''}
        confirmText="Send"
        confirmVariant="primary"
        onConfirm={() => nudgeGuestIds && handleConfirmBulkAction(nudgeGuestIds)}
        onClose={() => setNudgeGuestIds(null)}
        isLoading={sendEmail.isPending}
      />

      {/* Guest / Email upgrade gate */}
      <UpgradeModal
        isOpen={!!upgradeModal}
        onClose={() => setUpgradeModal(null)}
        error={upgradeModal ? upgradeGateError : null}
        inviteId={upgradeModal?.inviteId}
        headline={upgradeModal?.headline}
      />

      {/* Allergy Data Reminder Popup */}
      {showAllergyPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-background-card rounded-2xl border border-border p-6 max-w-md w-full shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-text-primary mb-1">Allergy Data Reminder</h3>
                <p className="text-sm text-text-secondary">Your event has passed and you have guest allergy data on record. We recommend reviewing and deleting this data to protect your guests' privacy.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => { localStorage.setItem('allergy_popup_shown', '1'); setShowAllergyPopup(false); }}
                className="text-sm text-text-secondary hover:text-text-primary"
              >
                Dismiss
              </button>
              <Link
                to="/app/guests/allergies"
                onClick={() => { localStorage.setItem('allergy_popup_shown', '1'); setShowAllergyPopup(false); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Manage Data
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar
        count={selection.count}
        actions={[
          ...(selectedWithEmail > 0 && (isAdmin || !singleInviteIsDraft)
            ? [{
                label: 'Send Email',
                tooltip: `Send invite email to ${selectedWithEmail} guest${selectedWithEmail !== 1 ? 's' : ''}`,
                icon: <Mail size={16} />,
                onClick: () => setShowBulkEmailConfirm(true),
                dataBuilder: 'guests-bulk-send-email',
              }]
            : []),
          ...(selectedNotInvited > 0
            ? [{
                label: 'Mark as Invited',
                tooltip: `Mark ${selectedNotInvited} guest${selectedNotInvited !== 1 ? 's' : ''} as invited`,
                icon: <Check size={16} />,
                onClick: handleBulkMarkAsInvited,
                dataBuilder: 'guests-bulk-mark-invited',
              }]
            : []),
          {
            label: 'Change RSVP',
            tooltip: `Change RSVP status for ${selection.count} guest${selection.count !== 1 ? 's' : ''}`,
            icon: <Users size={16} />,
            onClick: () => setShowBulkRsvpModal(true),
            dataBuilder: 'guests-bulk-change-rsvp',
          },
          {
            label: 'Invite',
            tooltip: `Assign ${selection.count} guest${selection.count !== 1 ? 's' : ''} to an invite`,
            icon: <ArrowRightLeft size={16} />,
            onClick: () => setShowBulkAssignModal(true),
            dataBuilder: 'guests-bulk-assign-invite',
          },
          ...(guestTags && guestTags.length > 0
            ? [{
                label: 'Assign Tag',
                tooltip: `Add a tag to ${selection.count} guest${selection.count !== 1 ? 's' : ''}`,
                icon: <Tag size={16} />,
                onClick: () => setShowBulkTagModal(true),
                dataBuilder: 'guests-bulk-assign-tag',
              }]
            : []),
          {
            label: 'Remove',
            tooltip: `Remove ${selection.count} selected guest${selection.count !== 1 ? 's' : ''}`,
            icon: <Trash2 size={16} />,
            onClick: () => setShowBulkRemoveConfirm(true),
            variant: 'danger' as const,
            dataBuilder: 'guests-bulk-remove',
          },
        ]}
        onClear={selection.clear}
      />
    </div>
  );
}
